import axios, { AxiosInstance } from 'axios';
import { useAuthStore } from '../stores/authStore';

// Determine API URL:
// - Production web (REACT_APP_API_URL=""): use relative URLs (empty string)
// - Development (REACT_APP_API_URL not set): fall back to localhost:8001
// - Mobile/Capacitor (capacitor:// or localhost on device): use production server
function getApiUrl(): string {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl !== undefined && envUrl !== '') return envUrl;
  
  // Check if running in Capacitor (native app) - relative URLs won't work
  const isCapacitor = typeof window !== 'undefined' && (
    window.location.protocol === 'capacitor:' ||
    (window.location.hostname === 'localhost' && window.location.port === '')
  );
  
  if (isCapacitor) return 'https://provisionmart.com';
  if (envUrl === '') return ''; // Production web: relative URLs via Caddy proxy
  return 'http://localhost:8000'; // Development fallback
}

const API_URL = getApiUrl();

const client: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ----------------------------------------------------------------------------
// In-flight GET de-duplication + tiny TTL cache
// ----------------------------------------------------------------------------
//
// Rationale: many React components mount in parallel and end up firing the
// SAME `GET /api/...` (cart summary, feature flags, categories, etc.) within
// a few milliseconds of each other. We coalesce identical in-flight GETs
// into a single network request, and optionally serve responses from a tiny
// 60 s memory cache for endpoints whitelisted via `cacheable=true`.
//
// Opt-out per-call:  client.get(url, { headers: { 'X-Skip-Dedupe': '1' } })
// Opt-in cache:      client.get(url, { headers: { 'X-Cache-Ttl': '30' } })  // seconds
//
// Mutations (POST/PUT/PATCH/DELETE) bypass everything.
// ----------------------------------------------------------------------------

interface CacheEntry { at: number; ttlMs: number; data: any; status: number; headers: any; }
const inflight = new Map<string, Promise<any>>();
const cache = new Map<string, CacheEntry>();

const keyFor = (cfg: any) => `${(cfg.method || 'get').toLowerCase()} ${cfg.baseURL || ''}${cfg.url || ''}?${JSON.stringify(cfg.params || {})}`;

client.interceptors.request.use((config) => {
  const authStore = useAuthStore.getState();
  if (authStore.accessToken) {
    config.headers.Authorization = `Bearer ${authStore.accessToken}`;
  }

  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') return config;

  const skip = config.headers?.['X-Skip-Dedupe'];
  if (skip) {
    delete config.headers['X-Skip-Dedupe'];
    return config;
  }

  const key = keyFor(config);

  // 1) Serve from cache if fresh
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < cached.ttlMs) {
    // Trick axios into resolving immediately by attaching an adapter that
    // returns the cached response synchronously.
    (config as any).adapter = () =>
      Promise.resolve({
        data: cached.data,
        status: cached.status,
        statusText: 'OK (cache)',
        headers: cached.headers,
        config,
        request: null,
      });
    return config;
  }

  // 2) Coalesce in-flight identical GETs
  const existing = inflight.get(key);
  if (existing) {
    (config as any).adapter = () => existing.then((res) => ({ ...res, config }));
  }
  // Otherwise, fall through; the response interceptor will record it.
  (config as any)._dedupeKey = key;
  return config;
});

// Handle token refresh on 401  +  populate dedupe map / cache
client.interceptors.response.use(
  (response) => {
    const cfg: any = response.config || {};
    const key: string | undefined = cfg._dedupeKey;
    const ttlHeader = cfg.headers?.['X-Cache-Ttl'];
    if (key) {
      // Settle the in-flight promise with this response so any waiters resolve.
      // (We only set inflight here, *after* the request actually fires.)
      const ttlMs = ttlHeader ? Number(ttlHeader) * 1000 : 0;
      if (ttlMs > 0) {
        cache.set(key, {
          at: Date.now(),
          ttlMs,
          data: response.data,
          status: response.status,
          headers: response.headers,
        });
      }
      inflight.delete(key);
    }
    return response;
  },
  async (error) => {
    const cfg: any = error.config || {};
    if (cfg._dedupeKey) inflight.delete(cfg._dedupeKey);

    if (error.response?.status === 401) {
      const authStore = useAuthStore.getState();
      const refreshToken = authStore.refreshToken;

      if (refreshToken) {
        try {
          // Try to refresh token
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          authStore.setTokens(access_token, refresh_token);

          // Retry the original request
          if (error.config) {
            error.config.headers.Authorization = `Bearer ${access_token}`;
            return client(error.config);
          }
        } catch (err) {
          authStore.logout();
          window.location.href = '/login';
        }
      } else {
        authStore.logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/** Clear the GET cache (e.g. after a mutation that invalidates it). */
export function clearApiCache(prefix?: string) {
  if (!prefix) { cache.clear(); return; }
  for (const k of Array.from(cache.keys())) {
    if (k.includes(prefix)) cache.delete(k);
  }
}

export default client;
